import React, { useState, useMemo, useCallback } from "react";
import ServiceHistoryTable from './ServiceHistoryTable';
import PaginationToolBar from '../common/PaginationToolBar';

const COLUMNS_PER_PAGE = 5;
const STATIC_COLUMN_COUNT = 1; // Only "Service Categories" is a permanent static column
const FIRST_STATIC_COLUMNS_COUNT = 3; // How many previousDates columns should be static before pagination begins

const NBAMultiVisitView = ({ vehicleNBA }) => {
    const [currentPage, setCurrentPage] = useState(0);

    // Helper function to generate columns
    const generateColumns = useCallback(() => {
         //* --- Service Category Columns ---
        const baseColumns = [
            {
                headerName: "Service Categories",
                headerAlign: "center",
                field: "category",
                flex: 1.2,
                sortable: false,
                renderCell: (params) => (
                    <div
                        style={{
                            flex: 1.4,
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                            overflow: "hidden",
                        }}
                    >
                        {params.value}
                    </div>
                ),
            },
        ];
        
        // If we don't have previousDates, return only the category column
        if (!vehicleNBA?.previousDates) return baseColumns;

        const { previousDates, date } = vehicleNBA;
        
        //* --- First N Previous Visit Page Columns ---
        const staticPreviousVisits = previousDates.slice(0, FIRST_STATIC_COLUMNS_COUNT).reverse();
        staticPreviousVisits .forEach((visitDate, index) => {
            baseColumns.push({
                headerName: visitDate,
                headerAlign: 'center',
                field: `previousVisit${staticPreviousVisits.length - index}`, // starts for the oldest inspection
                flex: 1, 
                sortable: false,
                align: 'center',
                renderCell: (params) => (
                    <div style={{
                        wordBreak: "break-word",
                        whiteSpace: "normal",
                        overflow: "hidden",
                        textAlign: "center",
                        }}
                    >
                        {params.value || ' '}
                    </div>
                ),
            });
        });

        //* --- Last Visit Column ---
        baseColumns.push({
            headerName: date,
            headerAlign: 'center',
            field: "lastVisit",
            flex: 1,
            sortable: false,
            align: 'center',
            renderCell: (params) => (
                <div style={{
                    wordBreak: "break-word",
                    whiteSpace: "normal",
                    overflow: "hidden",
                    textAlign: "center"
                    }}
                >
                    {params.value || ' '}
                </div>
            ),
        });

        //* --- Comments Column ---
        baseColumns.push({
            field: "comments",
            flex: 1.2,
            sortable: false,
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ 
                    lineHeight: '1.2em',
                    textAlign: 'center',
                    whiteSpace: 'normal',
                    fontWeight: 'bold',
                    }}
                >
                    <div>Comments from</div>
                    <div>{date}</div>
                </div>
            ),
            renderCell: (params) => (
                <div style={{
                    flex: 1.4,
                    textAlign: 'center',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                    }}
                >
                    {params.value || ' '}
                </div>
            ),
        });

        //* --- Paginated Previous Visit Columns (beyond the first N static ones) ---
        for (let i = FIRST_STATIC_COLUMNS_COUNT; i < previousDates.length; i += COLUMNS_PER_PAGE) {
            const page = previousDates.slice(i, i + COLUMNS_PER_PAGE).reverse();
            page.forEach((visitDate, index) => {
                baseColumns.push({
                    headerName: visitDate,
                    headerAlign: 'center',
                    field: `previousVisit${i + page.length - index}`,
                    flex: 1,
                    sortable: false,
                    align: 'center',
                    renderCell: (params) => (
                        <div style={{
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            overflow: 'hidden',
                            textAlign: "center"
                            }}
                        >
                            {params.value || ' '}
                        </div>
                    ),
                });
            });
        }
        
        return baseColumns;
    }, [vehicleNBA]);

    const columns = useMemo(() => generateColumns(), [generateColumns]);

    const paginatedColumns = useMemo(() => {
        // Exclude the first static column for pagination
        const dynamicColumns = columns.slice(STATIC_COLUMN_COUNT);
        const startIndex = currentPage * COLUMNS_PER_PAGE;
        const endIndex = startIndex + COLUMNS_PER_PAGE;
        return dynamicColumns.slice(startIndex, endIndex);
    }, [columns, currentPage]);

    const totalPages = Math.ceil((columns.length - STATIC_COLUMN_COUNT) / COLUMNS_PER_PAGE);

    const handlePageChange = useCallback((newPage) => {
        if (newPage >= 0 && newPage < totalPages) {
            setCurrentPage(newPage);
        }
    }, [totalPages]);

    // Combine static and paginated columns
    const displayedColumns = useMemo(() => {
        const staticColumns = columns.slice(0, STATIC_COLUMN_COUNT);
        return [...staticColumns, ...paginatedColumns];
    }, [columns, paginatedColumns]);

    return (
        <div>
            <ServiceHistoryTable
                columns={displayedColumns}
                data={vehicleNBA.status}
                className="NBA-multivisit-Grid"
                key="SH-NBA-Multivisit"
                paginationModel={{page: currentPage, pageSize: 8}}
                sx= {{"& .MuiDataGrid-row:hover": {backgroundColor: "transparent"}}}
            />
            {totalPages > 1 && (
                <PaginationToolBar
                    handlePageChange={handlePageChange}
                    currentPage={currentPage}
                    totalPages={totalPages}
                />
            )}
        </div>
    );
};

export default NBAMultiVisitView;
